import type { Ref } from "vue";
import type { User } from "~/models/User";
import { echo } from "@laravel/echo-vue";
import consola from "consola";

type Echo = ReturnType<typeof echo>;

export interface UseAppEcho {
    echo: Omit<
        Echo,
        | "connector"
        | "options"
        | "registerInterceptors"
        | "registerVueRequestInterceptor"
        | "registerAxiosRequestInterceptor"
        | "registerjQueryAjaxSetup"
        | "registerTurboRequestInterceptor"
    > & {
        connector: () => Echo["connector"];
        options: () => Echo["options"];
    };
    users: Ref<User[]>;
    channel: Ref<ReturnType<Echo["join"]>>;
}

export function useAppEcho(): UseAppEcho {
    const { $authenticatedUsers, $authenticatedChannel } = useNuxtApp();

    const logger = consola.create({
        level: 2,
        defaults: {
            tag: "echo",
        },
    });

    const appEcho = {
        connector: () => echo().connector,
        options: () => echo().options,
        channel: (channel: string) => echo().channel(channel),
        connect: () => {
            logger.log("Connecting to echo");
            return echo().connect();
        },
        disconnect: () => {
            logger.log("Disconnecting from echo");
            return echo().disconnect();
        },
        leave: (channel: string) => {
            logger.log(`Leaving channel ${channel}`);
            return echo().leave(channel);
        },
        leaveChannel: (channel: string) => {
            logger.log(`Leaving channel ${channel}`);
            return echo().leaveChannel(channel);
        },
        leaveAllChannels: () => {
            logger.log("Leaving all channels");
            return echo().leaveAllChannels();
        },
        listen: (
            channel: string,
            event: string,
            callback: CallableFunction,
        ) => {
            logger.log(`Listening to channel ${channel} for event ${event}`);
            return echo().listen(channel, event, callback);
        },
        private: (channel: string) => {
            logger.log(`Listening to private channel ${channel}`);
            return echo().private(channel);
        },
        encryptedPrivate: (channel: string) => {
            logger.log(`Listening to private channel ${channel}`);
            return echo().encryptedPrivate(channel);
        },
        socketId: () => {
            logger.log("Getting socket ID");
            return echo().socketId;
        },
    };

    return {
        echo: appEcho,
        users: $authenticatedUsers,
        channel: $authenticatedChannel,
    } as unknown as UseAppEcho;
}
